import { ChevronDown, Menu, Phone, X } from 'lucide-react'
import * as React from 'react'
import { useLocation } from 'react-router'

import { Logo } from '@/components/brand/Logo'
import { Container } from '@/components/Container'
import { Anchor } from '@/components/ui/Anchor'
import { Button } from '@/components/ui/button'
import { Link, linkVariants } from '@/components/ui/Link'
import { cn } from '@/lib/utils'

export type NavBarTheme = 'light' | 'dark'

export interface NavLinkSubItem {
  label: string
  /** Omit to render this item inert (e.g. "coming soon" — nothing to link to yet). */
  href?: string
  /** Opens the link in a new tab — use for destinations outside this app. */
  external?: boolean
  /** One-line explainer shown under the label in the submenu. */
  description?: string
  /** Short badge, e.g. "Coming soon". */
  tag?: string
}

export interface NavLink {
  label: string
  /** Omit when `items` is set — the trigger itself doesn't navigate, only its submenu items do. */
  href?: string
  /** When present, the link becomes a hover/focus-triggered dropdown trigger listing these instead of navigating anywhere itself. */
  items?: NavLinkSubItem[]
  /** Marks this as the current page/section — brand-green text instead of the default neutral. */
  active?: boolean
  /** Opens the link in a new tab — use for destinations outside this app. */
  external?: boolean
}

export interface NavBarProps {
  theme: NavBarTheme
  /** Destination for the logo link. Defaults to the marketing home (`/`); `null` renders the logo unlinked. */
  logoHref?: string | null
  /**
   * In-page section links (e.g. "About", "Products", "Blog"), rendered
   * between the logo and the utility/CTA cluster. On narrow viewports
   * these collapse behind a menu button into a drawer instead of
   * wrapping or squeezing the utility links and CTA. A link with `items`
   * renders as a submenu trigger instead of a plain link.
   */
  navLinks?: NavLink[]
  /**
   * Displayed phone number, e.g. "(770) 765-0331". Renders as its own
   * utility link with a phone icon, before `utilityLinks` and the CTA —
   * pass this instead of building a phone entry into `utilityLinks`.
   */
  phoneNumber?: string
  /** Digits-and-leading-plus tel: target. Defaults to `phoneNumber` with all non `[0-9+]` characters stripped. */
  phoneHref?: string
  onPhoneClick?: () => void
  /** Client-login / other utility links, in order, rendered after the phone number and before the CTA. */
  utilityLinks?: { label: string; icon: React.ReactNode; href: string; onClick?: () => void }[]
  /** Omit to render the nav with no CTA button at all. */
  ctaLabel?: string
  ctaHref?: string
  onCtaClick?: () => void
  /**
   * CSS selector (e.g. "#hero") for a section that visually competes with
   * the CTA's brand-green button — while it's in view, the nav CTA swaps
   * to the neutral variant instead of primary.
   */
  ctaNeutralOverSelector?: string
  /**
   * Disables the hide-on-scroll-down slide animation. For contexts where
   * this bar isn't acting as a real sticky page header — e.g. rendered
   * inline as a static reference on the design-system page — so it doesn't
   * react to the surrounding page's scroll.
   */
  disableScrollHide?: boolean
  /**
   * Replaces the utility-links/CTA cluster entirely with custom content
   * (e.g. a matched-broker card) — for pages whose right-side nav content
   * doesn't fit the plain link-list shape. Ignored alongside `navLinks`,
   * which isn't expected to be used with this.
   */
  rightSlot?: React.ReactNode
  /**
   * Page-level actions (e.g. Share and the cart), rendered at the right end after
   * `rightSlot` or the utility links and CTA. On phones the utility links shrink to
   * their icons to leave the row to these.
   */
  actions?: React.ReactNode
  /**
   * Line the nav content up with the page's `<Container>` instead of centering it in
   * the marketing `--maxw` column — for app pages whose body sits in one (e.g. the
   * Quotes page).
   */
  containerAligned?: boolean
  className?: string
}

/**
 * Marketing/product site nav bar. Both themes ship the same shape: logo,
 * 0+ in-page nav links, spacer, 0+ utility links, one primary CTA.
 *
 * Renders full-bleed (no rounded corners, no side borders) — a nav bar is
 * a page-width element, and the reference explicitly stopped rendering it
 * inside a rounded card-like container because that shape reads as an
 * enclosed component, not a page-spanning bar.
 */
export function NavBar({
  theme,
  logoHref = '/',
  navLinks = [],
  phoneNumber,
  phoneHref,
  onPhoneClick,
  utilityLinks = [],
  ctaLabel,
  ctaHref = '#',
  onCtaClick,
  ctaNeutralOverSelector,
  disableScrollHide = false,
  rightSlot,
  actions,
  containerAligned = false,
  className,
}: NavBarProps) {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
  const [isHidden, setIsHidden] = React.useState(false)
  const [isCtaNeutral, setIsCtaNeutral] = React.useState(false)
  const [openDropdown, setOpenDropdown] = React.useState<string | null>(null)
  const closeDropdownTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const navRef = React.useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const onDark = theme === 'dark'
  const logo = <Logo theme={onDark ? 'onDark' : 'black'} size="small" width={64} />
  const Content = containerAligned ? Container : 'div'

  const openDropdownNow = (label: string) => {
    if (closeDropdownTimeout.current) {
      clearTimeout(closeDropdownTimeout.current)
      closeDropdownTimeout.current = null
    }
    setOpenDropdown(label)
  }

  // Delayed close: the pointer crossing the visual gap between the trigger
  // and the panel below it briefly leaves both elements, firing this — if
  // it lands back on the trigger or panel (both re-fire enter) before the
  // timeout runs, the close never happens and the menu stays open.
  const scheduleCloseDropdown = (label: string) => {
    closeDropdownTimeout.current = setTimeout(() => {
      setOpenDropdown((current) => (current === label ? null : current))
    }, 200)
  }

  React.useEffect(() => {
    return () => {
      if (closeDropdownTimeout.current) clearTimeout(closeDropdownTimeout.current)
    }
  }, [])

  const renderUtilityLinks = (inDrawer?: boolean) => {
    const labelClassName = actions && !inDrawer ? 'max-sm:sr-only' : undefined
    return (
      <>
        {phoneNumber ? (
          <Link
            kind="util"
            icon={<Phone />}
            onDark={onDark}
            href={phoneHref ?? `tel:${phoneNumber.replace(/[^0-9+]/g, '')}`}
            onClick={onPhoneClick}
            className={inDrawer ? 'text-md' : undefined}
          >
            <span className={labelClassName}>{phoneNumber}</span>
          </Link>
        ) : null}
        {utilityLinks.map((link) => (
          <Link
            key={link.label}
            kind="util"
            icon={link.icon}
            onDark={onDark}
            href={link.href}
            onClick={link.onClick}
            className={inDrawer ? 'text-md' : undefined}
          >
            <span className={labelClassName}>{link.label}</span>
          </Link>
        ))}
      </>
    )
  }

  // The dropdown panel is always light but the drawer follows the theme, so the surface decides these.
  const renderSubItem = (item: NavLinkSubItem, onNavigate?: () => void, onDarkSurface = false) => {
    const itemClasses = cn(
      'block rounded-sm p-2 no-underline transition-colors duration-150',
      onDarkSurface ? 'text-white/74 hover:bg-white/10' : 'text-ink-600 hover:bg-ink-50'
    )
    const content = (
      <>
        <span className={cn('flex items-center gap-2 font-bold', onDarkSurface ? 'text-white' : 'text-ink')}>
          {item.label}
          {item.tag ? (
            <span
              className={cn(
                'rounded-full border px-1.5 py-0.5 font-mono text-[10px] tracking-widest uppercase',
                onDarkSurface ? 'border-white/30 text-white/60' : 'border-ink-200 text-ink-400'
              )}
            >
              {item.tag}
            </span>
          ) : null}
        </span>
        {item.description ? (
          <span
            className={cn(
              'mt-0.5 block text-sm leading-[1.4]',
              onDarkSurface ? 'text-white/60' : 'text-ink-500'
            )}
          >
            {item.description}
          </span>
        ) : null}
      </>
    )

    if (!item.href) {
      return (
        <div key={item.label} className={cn(itemClasses, 'cursor-default opacity-60 hover:bg-transparent')}>
          {content}
        </div>
      )
    }

    return (
      <Anchor
        key={item.label}
        className={itemClasses}
        href={item.href}
        target={item.external ? '_blank' : undefined}
        rel={item.external ? 'noopener noreferrer' : undefined}
        onClick={onNavigate}
      >
        {content}
      </Anchor>
    )
  }

  // The CTA switches to the neutral variant while the nav sits over the hero
  // image, so the brand-green button never lands on top of artwork.
  //
  // This used to ask "is at least 50% of the hero in the viewport", which made
  // the answer depend on the hero's HEIGHT — and therefore on when its image
  // decoded, since an undecoded <img> measures short. A tall hero (/insurance)
  // lands right on that 0.5 boundary, so the same page rendered the grey
  // `outline` button on some loads and the lime `default` one on others, with
  // no scroll to trigger a recheck. Ask what the design actually cares about
  // instead — does the hero still pass under the nav? — which is stable at any
  // hero height and independent of load timing.
  React.useEffect(() => {
    if (!ctaNeutralOverSelector) return
    // Re-resolved per route: the nav outlives a page change, so this would otherwise watch a detached node.
    const el = document.querySelector(ctaNeutralOverSelector)
    if (!el) {
      setIsCtaNeutral(false)
      return
    }

    const update = () => {
      // offsetHeight, not getBoundingClientRect(): the nav's hide-on-scroll
      // `-translate-y-full` would otherwise report a negative bottom and flip
      // the variant while the nav is off-screen.
      const navBottom = navRef.current?.offsetHeight ?? 0
      const rect = el.getBoundingClientRect()
      setIsCtaNeutral(rect.top <= navBottom && rect.bottom > navBottom)
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    // The hero's box grows when its image decodes or a font swaps in, so
    // recompute rather than trusting one pre-layout measurement.
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(el)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      resizeObserver.disconnect()
    }
  }, [ctaNeutralOverSelector, pathname])

  React.useEffect(() => {
    if (disableScrollHide) return
    let lastY = window.scrollY

    const handleScroll = () => {
      const currentY = window.scrollY
      const delta = currentY - lastY
      lastY = currentY

      if (isDrawerOpen || currentY <= 0) {
        setIsHidden(false)
      } else if (delta > 0) {
        setIsHidden(true)
      } else if (delta < 0) {
        setIsHidden(false)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isDrawerOpen, disableScrollHide])

  return (
    <nav
      ref={navRef}
      className={cn(
        // Fixed, not padding-derived: the tallest child differs per variant, so the bar used to jump between flows.
        // shrink-0: #root is a height-100% column flex, which otherwise squeezes the bar.
        'sticky top-0 z-100 h-14 shrink-0 transition-transform duration-300 md:h-16',
        // The Container carries its own gutter.
        !containerAligned && 'px-5.5',
        onDark ? 'bg-ink-900/90 backdrop-blur-md' : 'bg-ink-50/90 border-b border-white backdrop-blur-md',
        isHidden && '-translate-y-full',
        className
      )}
    >
      <Content
        className={cn('flex h-full items-center gap-4.5', !containerAligned && 'mx-auto max-w-(--maxw)')}
      >
        {logoHref === null ? (
          <span className="inline-flex leading-0">{logo}</span>
        ) : (
          <Anchor href={logoHref} className="inline-flex leading-0" aria-label="Cai home">
            {logo}
          </Anchor>
        )}
        {navLinks.length > 0 ? (
          <div className="ml-8 flex items-center gap-4.5 max-md:hidden">
            {navLinks.map((link) =>
              link.items && link.items.length > 0 ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => openDropdownNow(link.label)}
                  onMouseLeave={() => scheduleCloseDropdown(link.label)}
                >
                  <Button
                    type="button"
                    variant="iconOnly"
                    className={cn(
                      linkVariants({ kind: 'nav', onDark, active: link.active }),
                      'h-auto gap-1 rounded-none p-0 has-[>svg]:px-0 [&_svg:not([class*="size-"])]:size-3.5'
                    )}
                    aria-current={link.active ? 'page' : undefined}
                    aria-expanded={openDropdown === link.label}
                    onFocus={() => openDropdownNow(link.label)}
                    onClick={() => setOpenDropdown((current) => (current === link.label ? null : link.label))}
                  >
                    {link.label}
                    <ChevronDown aria-hidden="true" />
                  </Button>
                  {openDropdown === link.label ? (
                    <div className="border-ink-100 bg-surface absolute top-full left-0 z-200 mt-4 flex w-90 flex-col gap-1 rounded-xl border p-4 shadow-[0_16px_40px_rgba(0,0,0,0.12)]">
                      {link.items.map((item) => renderSubItem(item, () => setOpenDropdown(null)))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <Link
                  key={link.label}
                  kind="nav"
                  onDark={onDark}
                  href={link.href}
                  active={link.active}
                  aria-current={link.active ? 'page' : undefined}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>
        ) : null}
        {rightSlot ? (
          <div className="ml-auto flex items-center gap-4.5">
            {rightSlot}
            {actions}
          </div>
        ) : (
          <>
            <div className="ml-auto" />
            <div
              className={cn(
                'flex items-center gap-4.5',
                // Without navLinks there's no drawer to hold these, so they only give way on phones, and only to a CTA sharing the row.
                navLinks.length > 0 ? 'max-md:hidden' : ctaLabel && 'max-sm:hidden'
              )}
            >
              {renderUtilityLinks()}
            </div>
            {ctaLabel ? (
              <Button
                variant={isCtaNeutral ? 'outline' : 'default'}
                size="sm"
                href={ctaHref}
                onClick={onCtaClick}
                className={isCtaNeutral ? 'bg-ink-100 border-none' : undefined}
              >
                {ctaLabel}
              </Button>
            ) : null}
            {actions}
          </>
        )}
        {navLinks.length > 0 ? (
          <Button
            type="button"
            variant="iconOnly"
            size="icon-sm"
            className={cn('hidden p-1 max-md:flex', onDark ? 'text-white/74' : 'text-ink-600')}
            aria-label={isDrawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isDrawerOpen}
            onClick={() => setIsDrawerOpen((open) => !open)}
          >
            {isDrawerOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        ) : null}
      </Content>
      {navLinks.length > 0 && isDrawerOpen ? (
        <div
          className={cn(
            'absolute inset-x-0 top-full hidden max-h-[calc(100dvh-3.5rem)] flex-col gap-6 overflow-y-auto border-b border-white px-5.5 py-6 max-md:flex',
            onDark ? 'bg-ink-900' : 'bg-ink-50'
          )}
        >
          {navLinks.map((link) => (
            <div key={link.label} className="flex flex-col gap-4">
              {link.items && link.items.length > 0 ? (
                <span
                  className={cn(
                    'block',
                    linkVariants({ kind: 'nav', onDark, active: link.active }),
                    'text-xl'
                  )}
                >
                  {link.label}
                </span>
              ) : (
                <Link
                  kind="nav"
                  onDark={onDark}
                  href={link.href}
                  className="text-xl"
                  onClick={() => setIsDrawerOpen(false)}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                >
                  {link.label}
                </Link>
              )}
              {link.items && link.items.length > 0 ? (
                <div className="flex flex-col gap-4 pl-4">
                  {link.items.map((item) => renderSubItem(item, () => setIsDrawerOpen(false), onDark))}
                </div>
              ) : null}
            </div>
          ))}
          <div
            className={cn('flex flex-col gap-4 border-t pt-6', onDark ? 'border-white/12' : 'border-white')}
          >
            {renderUtilityLinks(true)}
          </div>
        </div>
      ) : null}
    </nav>
  )
}
